import React, { ChangeEvent, useEffect } from 'react';
import {
  CurrencyInput,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightAddon,
  Text,
  Tooltip,
} from '@wegroup/design-system';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CoverageFormValues } from '../../../../../types/Form';

const AddInsuranceModalInsuranceFormProvision: React.FC = () => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useFormContext<CoverageFormValues>();

  const premiumValue = watch('premium');
  const insuranceTax = watch('insurance_tax_percentage');

  useEffect(() => {
    const percentage = getValues('commission_percentage');
    if (percentage) {
      handlePercentChange(percentage);
    }
  }, [premiumValue, insuranceTax]);

  const handlePercentChange = (percent: number) => {
    const premium = getValues('premium');
    if (premium != null) {
      const taxPercentage = getValues('insurance_tax_percentage') || 0;
      const taxValue = premium * (taxPercentage / 100);
      const commissionValue = Number(
        ((premium - taxValue) * (percent / 100)).toFixed(2),
      );
      setValue('commission', commissionValue);
    } else {
      setValue('commission', undefined);
    }
  };
  const handleCurrencyChange = (value: number) => {
    const premium = getValues('premium');
    if (premium != null) {
      const taxPercentage = getValues('insurance_tax_percentage') || 0;
      const taxValue = premium * (taxPercentage / 100);
      const percentage = value / (premium - taxValue);
      setValue('commission_percentage', Number((percentage * 100).toFixed(2)));
    } else {
      setValue('commission_percentage', undefined);
    }
  };

  return (
    <FormControl
      isInvalid={!!errors.commission || !!errors.commission_percentage}
    >
      <FormLabel>
        <HStack spacing="1">
          <Text>{t('COMMISSION')}</Text>
          <Tooltip
            label={t('ADVISORY-REPORT_OFFERS_PROVISION_TOOLTIP')}
            shouldWrapChildren
          >
            <Icon name="GeneralActionsQuestion" boxSize={5} />
          </Tooltip>
        </HStack>
      </FormLabel>
      <HStack>
        <Controller
          name="commission_percentage"
          control={control}
          render={({ field: { onChange, name, value, ...fieldProps } }) => {
            return (
              <InputGroup>
                <Input
                  name={name}
                  type="number"
                  value={value || undefined}
                  placeholder="0,00"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const _value = Number(e.currentTarget.value);
                    onChange(_value || null);
                    handlePercentChange(_value);
                  }}
                  {...fieldProps}
                />
                <InputRightAddon bg="white" color="text.gray">
                  %
                </InputRightAddon>
              </InputGroup>
            );
          }}
        />
        <Icon name="GeneralActionsCompare" color="text.gray" />
        <Controller
          name="commission"
          control={control}
          render={({ field: { value, onChange, name, ...fieldProps } }) => {
            return (
              <CurrencyInput
                name={name}
                placeholder="0,00"
                onChange={({ value }) => {
                  if (document?.activeElement?.['name'] === name) {
                    onChange((value as number) || null);
                    handleCurrencyChange(value as number);
                  }
                }}
                value={value || undefined}
                {...fieldProps}
              />
            );
          }}
        />
      </HStack>
      {errors?.commission?.message && (
        <FormErrorMessage>{t(errors.commission.message)}</FormErrorMessage>
      )}
      {errors?.commission_percentage?.message && (
        <FormErrorMessage>
          {t(errors.commission_percentage.message)}
        </FormErrorMessage>
      )}
    </FormControl>
  );
};

export default AddInsuranceModalInsuranceFormProvision;
